import React, { useContext, useEffect } from "react";

/**
 * How a screen takes the foot of a phone for itself.
 *
 * The tab bar is the shell's, and every screen lives above it — except the ones
 * that are not really screens but jobs: a long form with its own Save and
 * Cancel pinned across the bottom. Two bars stacked at the foot of a phone is
 * one bar too many, and the space between them reads as a gap in the layout
 * rather than as a boundary. Worse, the lower of the two is the one offering to
 * leave the form.
 *
 * So a form says it is holding the bottom edge, and the shell stops drawing the
 * bar for as long as it is. The rail is still a press away behind the avatar in
 * the top bar, and the guard on it still asks about the form before it goes.
 *
 * Claims are counted rather than flagged: a screen mounting its replacement
 * while another is still tearing down must not leave the bar hidden for good,
 * and React runs an effect twice on mount in development.
 *
 * Only a section root has a bar to claim, since that is the whole of where one
 * is drawn — see `barShown` in `AppLayout`. The claim is kept for those: the
 * day's own pinned row on My task is one, and a form that is a section root
 * tomorrow would be another. Everywhere else it is harmless and costs a
 * counter, which is cheaper than a form having to know which kind of screen it
 * is being opened as.
 */
export interface TabBarVisibility {
  /** Hides the bar, and answers with the release that puts it back. */
  hide: () => () => void;
}

/** Provided by `AppLayout`, which is what holds the count and draws the bar. */
export const TabBarVisibilityContext =
  React.createContext<TabBarVisibility | null>(null);

/**
 * Hides the phone's tab bar for as long as the calling component is mounted.
 *
 * Mount it with the thing that is taking the bottom edge — the profile form
 * mounts only while it is being edited, so the form itself is the claim. No
 * width check: above `md` the bar is not drawn at all, so hiding it there costs
 * nothing and the screen does not have to ask how wide it is.
 *
 * Outside the shell — a test rendering a form on its own, an auth screen — the
 * context is absent and this does nothing.
 */
export const useHiddenTabBar = (enabled = true): void => {
  const visibility = useContext(TabBarVisibilityContext);
  const hide = visibility?.hide;

  // `hide` answers with its own release, which is exactly what an effect
  // wants back — and it is stable, so this runs once per mounting.
  //
  // `enabled` is for a component that is sometimes the bottom edge and
  // sometimes a row above one — `PinnedActionBar` is both, depending on
  // whether the screen around it has already claimed the edge for itself. A
  // flag rather than a conditional call, because a hook cannot be one; turning
  // it off releases the claim the same way unmounting does.
  useEffect(() => (enabled ? hide?.() : undefined), [hide, enabled]);
};
